// src/components/module/ventas/VentaDetailModal.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  IconButton,
  Divider,
  Stack,
  Fade,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  Badge as BadgeIcon,
  CalendarToday,
  ReceiptLong,
  Phone as PhoneIcon,
  Home as HomeIcon,
  CreditCard,
  ShoppingCart,
  Discount as DiscountIcon,
  CheckCircleOutline,
  Schedule,
  CancelOutlined,
  Receipt,
} from "@mui/icons-material";
import { formatMoney, formatDate } from "../../../utils/format";
import {
  printService,
} from "../../../services/api/ptint/PrintService";
import { SelectorImpresion } from "./SelectorImpresion";
import { toast } from "../../../utils/toast";
import { OpcionesImpresion } from "../../../components/types/impresion.types";
import { DarkModeContext } from "../../../context/darkModeContext";
import PanelDocumentoDian from './PanelDocumentoDian';

const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F",
    textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7",
    accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB",
    warning: "#D9730D", warningBg: "#FADEC9", error: "#E03E3E", errorBg: "#FBE4E4",
    hover: "rgba(55,53,47,0.04)",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6",
    textS: "#9B9A97", textT: "#6B6B6B", border: "#333333",
    accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A",
    warning: "#C77D48", warningBg: "#2E2215", error: "#D44C47", errorBg: "#3D2020",
    hover: "rgba(255,255,255,0.04)",
  },
};

const getEstadoStyle = (estado: string, dark: boolean) => {
  const p = dark ? P.dark : P.light;
  const s = estado.toLowerCase();
  if (s === "pagada" || s === "finalizada") return { bg: p.successBg, color: p.success, dot: p.success };
  if (s === "pendiente") return { bg: p.warningBg, color: p.warning, dot: p.warning };
  if (s === "anulada") return { bg: p.errorBg, color: p.error, dot: p.error };
  return { bg: p.accentBg, color: p.accent, dot: p.accent };
};

interface ArticuloVenta {
  Id: number; Nombre: string; EAN: string; PrecioVenta: number;
  Cantidad: number; Descuento: number; Valor: number;
  Impuesto: Array<{ Id: number; Nombre: string; Valor: number }>;
}

interface DetalleVenta {
  id: number; fecha: string; numeroFactura: string;
  cliente: { id: number; nombre: string; identificacion: string; direccion: string; telefono: string };
  sucursal: { id: number; nombre: string };
  vendedor: { id: number; nombre: string };
  articulos: ArticuloVenta[];
  subtotal: number; descuentoTotal: number;
  impuestos: Array<{ nombre: string; valor: number }>;
  totalAPagar: number; saldoPendiente: number; estado: string;
  tipoPago: { id: number; nombre: string }; observaciones?: string;
}

interface VentaDetailModalProps {
  venta: DetalleVenta | null;
  open: boolean;
  onClose: () => void;
}

// Info Item
const InfoItem: React.FC<{ icon: React.ReactElement; label: string; value: string; color: string; textColor: string }> = ({ icon, label, value, color, textColor }) => (
  <Box>
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.3 }}>
      <Box sx={{ color, "& svg": { fontSize: 13 } }}>{icon}</Box>
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontSize: "0.88rem", fontWeight: 500, color: textColor }}>{value || "—"}</Typography>
  </Box>
);

export const VentaDetailModal: React.FC<VentaDetailModalProps> = ({ venta, open, onClose }) => {
  const [showImpresion, setShowImpresion] = useState(false);
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  if (!venta) return null;

  const est = getEstadoStyle(venta.estado, darkMode);
  const thStyle = { fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase" as const, letterSpacing: "0.04em", borderBottom: `1px solid ${p.border}`, py: 1, bgcolor: p.bgS };
  const tdStyle = { borderBottom: `1px solid ${p.border}`, py: 1.2, fontSize: "0.84rem" };

  const handleImprimir = async (opciones: OpcionesImpresion) => {
    try {
      await printService.imprimirVenta(venta, {
        ...opciones,
        configuracionEmpresa: { nombre: "Empresa", nit: "", direccion: "", telefono: "3002428638", email: "empresa@mail.com", sitioWeb: "" },
      });
      toast.success("Enviado a impresión");
      setShowImpresion(false);
    } catch { toast.error("No se pudo imprimir"); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{
        sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden" },
      }}>
        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}` }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 42, height: 42, borderRadius: "10px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", color: p.accent }}>
              <ReceiptLong sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: p.text }}>
                  Factura N° {venta.numeroFactura}
                </Typography>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.4, borderRadius: "6px", bgcolor: est.bg, color: est.color, fontSize: "0.78rem", fontWeight: 600 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: est.dot }} />
                  {venta.estado}
                </Box>
              </Stack>
              <Typography sx={{ fontSize: "0.78rem", color: p.textT, mt: 0.2 }}>{formatDate(venta.fecha)}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <IconButton onClick={() => setShowImpresion(true)} sx={{ width: 34, height: 34, borderRadius: "8px", color: p.textS, "&:hover": { bgcolor: p.bgT } }}>
              <PrintIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton onClick={onClose} sx={{ width: 34, height: 34, borderRadius: "8px", color: p.textT, "&:hover": { bgcolor: p.bgT } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
          <Fade in timeout={300}>
            <Box>
              {/* Info cards grid */}
              <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {/* Info General */}
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, height: "100%" }}>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 650, color: p.text, mb: 2 }}>Información General</Typography>
                    <Stack spacing={2}>
                      <InfoItem icon={<CalendarToday />} label="Fecha" value={formatDate(venta.fecha)} color={p.textT} textColor={p.text} />
                      <InfoItem icon={<StoreIcon />} label="Sucursal" value={venta.sucursal.nombre} color={p.textT} textColor={p.text} />
                      <InfoItem icon={<BadgeIcon />} label="Vendedor" value={venta.vendedor.nombre} color={p.textT} textColor={p.text} />
                    </Stack>
                  </Paper>
                </Grid>

                {/* Cliente */}
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, height: "100%" }}>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 650, color: p.text, mb: 2 }}>Cliente</Typography>
                    <Stack spacing={2}>
                      <InfoItem icon={<PersonIcon />} label="Nombre" value={venta.cliente.nombre} color={p.textT} textColor={p.text} />
                      <InfoItem icon={<BadgeIcon />} label="Identificación" value={venta.cliente.identificacion} color={p.textT} textColor={p.text} />
                      <InfoItem icon={<HomeIcon />} label="Dirección" value={venta.cliente.direccion} color={p.textT} textColor={p.text} />
                      <InfoItem icon={<PhoneIcon />} label="Teléfono" value={venta.cliente.telefono} color={p.textT} textColor={p.text} />
                    </Stack>
                  </Paper>
                </Grid>

                {/* Pago + Totales */}
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, height: "100%" }}>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 650, color: p.text, mb: 2 }}>Resumen de Pago</Typography>
                    <Stack spacing={1.2}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: "0.82rem", color: p.textS }}>Subtotal</Typography>
                        <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: p.text, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(venta.subtotal)}</Typography>
                      </Stack>
                      {venta.descuentoTotal > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography sx={{ fontSize: "0.82rem", color: p.error }}>Descuento</Typography>
                          <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: p.error, fontFamily: "'IBM Plex Mono', monospace" }}>-{formatMoney(venta.descuentoTotal)}</Typography>
                        </Stack>
                      )}
                      {venta.impuestos.map((imp, i) => (
                        <Stack key={i} direction="row" justifyContent="space-between">
                          <Typography sx={{ fontSize: "0.82rem", color: p.textS }}>{imp.nombre}</Typography>
                          <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: p.text, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(imp.valor)}</Typography>
                        </Stack>
                      ))}
                      <Divider sx={{ borderColor: p.border }} />
                      <Box sx={{ p: 2, borderRadius: "8px", bgcolor: p.accentBg, border: `1px solid ${alpha(p.accent, 0.2)}` }}>
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", mb: 0.3 }}>Total</Typography>
                        <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, color: p.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(venta.totalAPagar)}</Typography>
                      </Box>
                      <InfoItem icon={<CreditCard />} label="Método de Pago" value={venta.tipoPago.nombre} color={p.textT} textColor={p.text} />
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              {/* Artículos */}
              <Paper elevation={0} sx={{ borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden", mb: 3 }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${p.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ShoppingCart sx={{ fontSize: 16, color: p.accent }} />
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 650, color: p.text }}>Artículos ({venta.articulos.length})</Typography>
                  </Stack>
                </Box>
                <TableContainer sx={{ maxHeight: 350 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {["EAN", "Artículo", "Cant.", "Precio Unit.", "Dcto.", "Impuesto", "Subtotal"].map((h, i) => (
                          <TableCell key={h} align={i >= 2 ? "right" : "left"} sx={thStyle}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {venta.articulos.map((art) => {
                        const sub = art.PrecioVenta * art.Cantidad;
                        const desc = sub * ((art.Descuento || 0) / 100);
                        const subD = sub - desc;
                        return (
                          <TableRow key={art.Id || Math.random()} sx={{ "&:hover": { bgcolor: p.hover } }}>
                            <TableCell sx={{ ...tdStyle, fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.78rem", color: p.textS }}>{art.EAN || "—"}</TableCell>
                            <TableCell sx={{ ...tdStyle, fontWeight: 500, color: p.text }}>{art.Nombre}</TableCell>
                            <TableCell align="right" sx={{ ...tdStyle, fontWeight: 600 }}>{art.Cantidad}</TableCell>
                            <TableCell align="right" sx={{ ...tdStyle, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(art.PrecioVenta)}</TableCell>
                            <TableCell align="right" sx={tdStyle}>
                              {art.Descuento > 0 ? (
                                <Box sx={{ display: "inline-flex", px: 0.8, py: 0.1, borderRadius: "4px", bgcolor: p.errorBg, color: p.error, fontSize: "0.75rem", fontWeight: 600 }}>{art.Descuento}%</Box>
                              ) : <Typography sx={{ color: p.textT, fontSize: "0.82rem" }}>—</Typography>}
                            </TableCell>
                            <TableCell align="right" sx={{ ...tdStyle, fontSize: "0.78rem", color: p.textS }}>
                              {art.Impuesto?.length > 0 ? art.Impuesto.map(i => `${i.Nombre} ${i.Valor}%`).join(", ") : "—"}
                            </TableCell>
                            <TableCell align="right" sx={tdStyle}>
                              <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(subD)}</Typography>
                              {art.Descuento > 0 && <Typography sx={{ fontSize: "0.7rem", textDecoration: "line-through", color: p.textT }}>{formatMoney(sub)}</Typography>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Facturación electrónica DIAN */}
              <PanelDocumentoDian idVenta={venta.id} numeroFactura={venta.numeroFactura} />

              {/* Observaciones */}
              {venta.observaciones && (
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg }}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>Observaciones</Typography>
                  <Typography sx={{ fontSize: "0.84rem", color: p.textS }}>{venta.observaciones}</Typography>
                </Paper>
              )}
            </Box>
          </Fade>
        </DialogContent>

        <SelectorImpresion open={showImpresion} onClose={() => setShowImpresion(false)} onImprimir={handleImprimir} sucursalId={venta.sucursal.id} venta={venta} />
      </Dialog>
  );
};
